import React from "react";
import {
  MdContentCopy,
  MdCheck,
  MdFileDownload,
  MdInsertDriveFile,
  MdError,
  MdWarning,
  MdTrendingUp,
  MdTrendingDown,
  MdTrendingFlat,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import { ChatMessage, SummaryMetric } from "./mockData";

const ROWS_PER_PAGE = 5;

/** Cap decimal places at 2 for display. Non-numeric strings pass through unchanged. */
function formatDisplayValue(val: unknown): string {
  const str = String(val ?? "");
  const num = Number(str);
  if (str !== "" && !isNaN(num) && str.includes(".") && (str.split(".")[1] || "").length > 2) {
    return parseFloat(num.toFixed(2)).toString();
  }
  return str;
}

// --- Sub-components ---

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function FileCard(props: {
  filename: string;
  size: string;
  content: string;
  fileType?: "csv" | "pdf";
}) {
  const { filename, size, content, fileType = "csv" } = props;
  const isPDF = fileType === "pdf";

  const handleClick = () => {
    const mimeType = isPDF ? "application/pdf" : "text/csv;charset=utf-8;";
    downloadFile(content, filename, mimeType);
  };

  return (
    <div
      onClick={handleClick}
      className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:hover:bg-navy-700"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          isPDF
            ? "bg-red-100 dark:bg-red-900/30"
            : "bg-green-100 dark:bg-green-900/30"
        }`}
      >
        <MdInsertDriveFile
          className={`h-5 w-5 ${
            isPDF
              ? "text-red-600 dark:text-red-400"
              : "text-green-600 dark:text-green-400"
          }`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
          {filename}
        </p>
        <p className="text-xs text-gray-400">
          {isPDF ? "PDF" : "CSV"} &middot; {size}
        </p>
      </div>
      <MdFileDownload className="h-5 w-5 shrink-0 text-gray-400" />
    </div>
  );
}

function SummaryCard(props: {
  label: string;
  value: string;
  change: string;
  changeType: "up" | "down" | "neutral";
}) {
  const { label, value, change, changeType } = props;
  const changeColor =
    changeType === "up"
      ? "text-green-500"
      : changeType === "down"
      ? "text-red-500"
      : "text-gray-400";
  const ChangeIcon =
    changeType === "up"
      ? MdTrendingUp
      : changeType === "down"
      ? MdTrendingDown
      : MdTrendingFlat;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
        {formatDisplayValue(value)}
      </p>
      <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${changeColor}`}>
        <ChangeIcon className="h-3.5 w-3.5" />
        {change}
      </div>
    </div>
  );
}

function TableView(props: {
  headers: string[];
  rows: string[][];
  csvContent: string;
}) {
  const { headers, rows, csvContent } = props;
  const [page, setPage] = React.useState(0);
  const [copied, setCopied] = React.useState(false);

  const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);
  const start = page * ROWS_PER_PAGE;
  const visibleRows = rows.slice(start, start + ROWS_PER_PAGE);

  const handleCopyCSV = async () => {
    try {
      await navigator.clipboard.writeText(csvContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-600">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-navy-900">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="whitespace-nowrap px-3 py-2 font-semibold text-navy-700 dark:text-white"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, ri) => (
              <tr
                key={start + ri}
                className={`border-t border-gray-100 dark:border-navy-700 ${
                  ri % 2 === 0
                    ? "bg-white dark:bg-navy-800"
                    : "bg-gray-50/50 dark:bg-navy-800/50"
                }`}
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="whitespace-nowrap px-3 py-2 text-navy-700 dark:text-gray-200"
                  >
                    {formatDisplayValue(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer: actions + pagination */}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopyCSV}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-gray-400 transition hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-600 dark:hover:text-white"
          >
          {copied ? (
            <>
              <MdCheck className="h-3 w-3" />
              Copied CSV
            </>
          ) : (
            <>
              <MdContentCopy className="h-3 w-3" />
              Copy as CSV
            </>
          )}
          </button>
          <button
            onClick={() => downloadFile(csvContent, "latte-table-export.csv", "text/csv;charset=utf-8;")}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-gray-400 transition hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-600 dark:hover:text-white"
          >
            <MdFileDownload className="h-3 w-3" />
            .csv
          </button>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-navy-600"
            >
              <MdChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[11px] font-medium text-gray-400">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-navy-600"
            >
              <MdChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ListView(props: { items: string[] }) {
  const { items } = props;
  const [showAll, setShowAll] = React.useState(false);
  const visibleItems = showAll ? items : items.slice(0, ROWS_PER_PAGE);
  const hasMore = items.length > ROWS_PER_PAGE;

  return (
    <div>
      <ul className="space-y-1.5">
        {visibleItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-navy-700 dark:text-white">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
            {item}
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-2 text-xs font-bold text-brand-500 transition hover:underline hover:text-brand-600 dark:text-brand-400"
        >
          {showAll ? "Show less" : `Show ${items.length - ROWS_PER_PAGE} more`}
        </button>
      )}
    </div>
  );
}

function ErrorCard(props: {
  message: string;
  level: "error" | "warning";
  retryQuery?: string;
  retryable?: boolean;
  onRetry?: (query: string) => void;
}) {
  const { message, level, retryQuery, retryable, onRetry } = props;
  const isError = level === "error";
  return (
    <div
      className={`rounded-xl border p-3 ${
        isError
          ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20"
          : "border-yellow-200 bg-yellow-50 dark:border-yellow-900/50 dark:bg-yellow-900/20"
      }`}
    >
      <div className="flex items-start gap-3">
        {isError ? (
          <MdError className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        ) : (
          <MdWarning className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" />
        )}
        <p
          className={`text-sm ${
            isError
              ? "text-red-700 dark:text-red-300"
              : "text-yellow-700 dark:text-yellow-300"
          }`}
        >
          {message}
        </p>
      </div>
      {retryable !== false && retryQuery && onRetry && (
        <button
          onClick={() => onRetry(retryQuery)}
          className={`mt-2 w-full rounded-lg py-1.5 text-xs font-bold transition ${
            isError
              ? "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
              : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50"
          }`}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// --- Main component ---

function QueryMessage(props: { message: ChatMessage; onRetry?: (query: string) => void }) {
  const { message, onRetry } = props;
  const isUser = message.sender === "user";
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available (e.g. non-HTTPS context)
    }
  };

  const handleDownload = () => {
    const filename = `latte-query-${message.id}.csv`;
    downloadFile(message.content, filename, "text/csv;charset=utf-8;");
  };

  const renderContent = () => {
    switch (message.type) {
      case "file":
        return (
          <FileCard
            filename={message.fileName}
            size={message.fileSize}
            content={message.content}
            fileType={message.fileType}
          />
        );

      case "summary": {
        // Support legacy messages stored with old summaryLabel/summaryValue fields
        const metrics: SummaryMetric[] = message.summaryMetrics ||
          [{ label: (message as any).summaryLabel, value: (message as any).summaryValue, change: (message as any).summaryChange, changeType: (message as any).summaryChangeType || "neutral" }];
        return (
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(metrics.length, 3)}, 1fr)` }}>
            {metrics.map((m, i) => (
              <SummaryCard
                key={i}
                label={m.label}
                value={m.value}
                change={m.change}
                changeType={m.changeType}
              />
            ))}
          </div>
        );
      }

      case "table":
        return (
          <TableView
            headers={message.tableHeaders}
            rows={message.tableRows}
            csvContent={message.content}
          />
        );

      case "list":
        return <ListView items={message.listItems} />;

      case "error":
        return (
          <ErrorCard
            message={message.content}
            level={message.errorLevel}
            retryQuery={message.retryQuery}
            retryable={message.retryable}
            onRetry={onRetry}
          />
        );

      case "csv":
        return (
          <div className="group relative">
            <div className="absolute right-0 top-0 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 rounded-md bg-white/80 px-2 py-1 text-xs text-navy-700 hover:bg-white dark:bg-navy-600/80 dark:text-white dark:hover:bg-navy-600"
              >
                {copied ? (
                  <>
                    <MdCheck className="h-3 w-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <MdContentCopy className="h-3 w-3" />
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1 rounded-md bg-white/80 px-2 py-1 text-xs text-navy-700 hover:bg-white dark:bg-navy-600/80 dark:text-white dark:hover:bg-navy-600"
              >
                <MdFileDownload className="h-3 w-3" />
                .csv
              </button>
            </div>
            <pre className="overflow-x-auto whitespace-pre text-xs leading-5 text-navy-700 dark:text-white font-mono">
              {message.content}
            </pre>
          </div>
        );

      default:
        return (
          <p
            className={`text-sm whitespace-pre-line ${
              isUser ? "text-white" : "text-navy-700 dark:text-white"
            }`}
          >
            {message.content}
          </p>
        );
    }
  };

  return (
    <div className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`${
          message.type === "table" ? "max-w-[95%]" : "max-w-[85%]"
        } p-4 ${
          isUser
            ? "rounded-l-xl rounded-br-xl bg-brand-500 text-white"
            : "rounded-r-xl rounded-bl-xl bg-lightPrimary dark:bg-navy-700"
        }`}
      >
        {renderContent()}
        <p
          className={`mt-2 text-xs font-medium ${
            isUser ? "text-white/70" : "text-gray-400 dark:text-gray-300"
          }`}
        >
          {(() => {
            const parsed = new Date(message.timestamp);
            if (isNaN(parsed.getTime())) return message.timestamp;
            return parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
          })()}
        </p>
      </div>
    </div>
  );
}

export default QueryMessage;
